import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close,
  Visibility,
  OpenInNew,
  Forum,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGetProductCommentsQuery, useGetProductQuery, useGetProductCommentThreadsQuery } from '../../store/api/adminApi';

const ProductCommentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [productDetailDialog, setProductDetailDialog] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [threadDialog, setThreadDialog] = useState(false);
  const [threadProductId, setThreadProductId] = useState<string>('');
  const [threadProductName, setThreadProductName] = useState<string>('');

  const { data: commentsData, isLoading, error } = useGetProductCommentsQuery({
    page: page + 1,
    limit: rowsPerPage,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const { data: productDetail, isLoading: isLoadingProduct } = useGetProductQuery(
    selectedProductId,
    { skip: !selectedProductId }
  );

  const { data: threadData, isLoading: isLoadingThreads } = useGetProductCommentThreadsQuery(
    threadProductId,
    { skip: !threadProductId || !threadDialog }
  );

  const handleViewThreads = (productId: string, productName: string) => {
    setThreadProductId(productId);
    setThreadProductName(productName);
    setThreadDialog(true);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewProduct = (productId: string) => {
    setSelectedProductId(productId);
    setProductDetailDialog(true);
  };

  const handleGoToProductPage = (productId: string) => {
    navigate(`/products?search=${productId}`);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE': return <Chip label="出品中" size="small" color="success" />;
      case 'DELETED': return <Chip label="削除済み" size="small" color="error" />;
      case 'SELLER_DELETED': return <Chip label="出品者削除" size="small" color="error" />;
      case 'REJECTED': return <Chip label="却下" size="small" color="error" />;
      case 'HIDDEN': return <Chip label="非表示" size="small" color="default" />;
      default: return <Chip label="販売中" size="small" color="success" />;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">コメントの取得に失敗しました。</Alert>
      </Box>
    );
  }

  const comments = commentsData?.items || [];
  const totalCount = commentsData?.totalCount || 0;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">
          商品コメント一覧
        </Typography>
        <Typography variant="body2" color="text.secondary">
          全 {totalCount} 件
        </Typography>
      </Box>

      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>商品</TableCell>
                <TableCell>コメント投稿者</TableCell>
                <TableCell sx={{ minWidth: 300 }}>コメント内容</TableCell>
                <TableCell>投稿日時</TableCell>
                <TableCell>商品ステータス</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" py={4}>
                      コメントがありません
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                comments.map((comment: any) => (
                  <TableRow key={comment.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Avatar
                          src={comment.productImage}
                          variant="rounded"
                          sx={{ width: 48, height: 48 }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight="bold" sx={{
                            maxWidth: 150,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {comment.productName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ¥{comment.productPrice?.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{comment.commenterName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {comment.commenterId?.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{
                        maxWidth: 400,
                        wordBreak: 'break-word',
                      }}>
                        {comment.comment}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {comment.createdAt
                          ? new Date(comment.createdAt).toLocaleString('ja-JP')
                          : '不明'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusLabel(comment.productStatus)}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Forum />}
                          onClick={() => handleViewThreads(comment.productId, comment.productName)}
                        >
                          全コメント
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={() => handleViewProduct(comment.productId)}
                        >
                          商品詳細
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
        />
      </Paper>

      {/* 商品詳細ダイアログ */}
      <Dialog
        open={productDetailDialog}
        onClose={() => setProductDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          商品詳細
          <IconButton
            onClick={() => setProductDetailDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingProduct ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : productDetail ? (
            <Box>
              <Box display="flex" gap={3} mb={3}>
                {productDetail.images && productDetail.images.length > 0 && (
                  <Box>
                    <img
                      src={productDetail.images[0]}
                      alt={productDetail.productName}
                      style={{ width: 200, height: 200, objectFit: 'cover', borderRadius: 8 }}
                    />
                    {productDetail.images.length > 1 && (
                      <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                        {productDetail.images.slice(1, 5).map((img: string, idx: number) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`${productDetail.productName} ${idx + 2}`}
                            style={{ width: 45, height: 45, objectFit: 'cover', borderRadius: 4 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
                <Box flex={1}>
                  <Typography variant="h6" gutterBottom>{productDetail.productName}</Typography>
                  <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                    ¥{productDetail.price?.toLocaleString()}
                  </Typography>
                  {getStatusLabel(productDetail.status)}

                  <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 1, mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">商品ID</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{productDetail.id}</Typography>

                    <Typography variant="body2" color="text.secondary">出品者ID</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{productDetail.userId}</Typography>

                    <Typography variant="body2" color="text.secondary">カテゴリ</Typography>
                    <Typography variant="body2">{productDetail.category || '未設定'}</Typography>

                    <Typography variant="body2" color="text.secondary">出品日</Typography>
                    <Typography variant="body2">
                      {productDetail.createdAt ? new Date(productDetail.createdAt).toLocaleString('ja-JP') : '不明'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {productDetail.description && (
                <Box mt={2}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>商品説明</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {productDetail.description}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">商品情報を取得できませんでした。</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<OpenInNew />}
            onClick={() => {
              handleGoToProductPage(selectedProductId);
              setProductDetailDialog(false);
            }}
          >
            商品管理ページで開く
          </Button>
          <Button onClick={() => setProductDetailDialog(false)}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* コメントスレッドダイアログ */}
      <Dialog
        open={threadDialog}
        onClose={() => { setThreadDialog(false); setThreadProductId(''); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          コメント一覧 — {threadProductName}
          <IconButton
            onClick={() => { setThreadDialog(false); setThreadProductId(''); }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingThreads ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : threadData?.threads && threadData.threads.length > 0 ? (
            <Box>
              {threadData.threads.map((thread: any, tIdx: number) => (
                <Paper key={tIdx} variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" mb={1}>
                    {thread.userName} のコメントスレッド
                  </Typography>
                  <Box sx={{ pl: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
                    {thread.replies.map((reply: any, rIdx: number) => (
                      <Box key={rIdx} sx={{ py: 1, borderBottom: rIdx < thread.replies.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="caption" fontWeight="bold" color="text.primary">
                            {reply.userName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {reply.createdAt ? new Date(reply.createdAt).toLocaleString('ja-JP') : ''}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {reply.comment}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary" textAlign="center" py={4}>
              コメントがありません
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setThreadDialog(false); setThreadProductId(''); }}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductCommentsPage;
